import { ReactTable, type RowData } from '@/lib/tanstack-table';
import { useSelector } from '@tanstack/react-store';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
} from '@phosphor-icons/react';
import { T, Var, Plural } from 'gt-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DataTablePaginationProps<TData extends RowData> {
  table: ReactTable<TData>;
  showSelectedRowCount?: boolean;
  /** Override for page count - use when table.getPageCount() doesn't update reactively */
  pageCount?: number;
  /** Override for page index - use when table.state.pagination.pageIndex doesn't update reactively */
  pageIndex?: number;
  /** Callback for page changes - use when table methods don't work with manual pagination */
  onPageChange?: (pageIndex: number) => void;
  /** Rows-per-page choices. Defaults to the shared 10–50 ladder. */
  pageSizeOptions?: readonly number[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 25, 30, 40, 50] as const;

export function DataTablePagination<TData extends RowData>({
  table,
  showSelectedRowCount = false,
  pageCount: pageCountProp,
  pageIndex: pageIndexProp,
  onPageChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: DataTablePaginationProps<TData>) {
  const tableState = useSelector(table.store, (state) => state);
  const pageCount = pageCountProp ?? table.getPageCount();
  const pageIndex = pageIndexProp ?? tableState.pagination.pageIndex;
  const filteredSelectedRowCount =
    table.getFilteredSelectedRowModel().rows.length;
  const filteredRowCount = table.getFilteredRowModel().rows.length;

  // When pageCount is provided as prop, calculate these ourselves
  // since table's internal state may not reflect the correct pageCount
  const canPreviousPage =
    pageCountProp !== undefined ? pageIndex > 0 : table.getCanPreviousPage();
  const canNextPage =
    pageCountProp !== undefined
      ? pageIndex < pageCount - 1
      : table.getCanNextPage();

  const goToPage = (newPageIndex: number) => {
    if (onPageChange) {
      onPageChange(newPageIndex);
    } else {
      table.setPageIndex(newPageIndex);
    }
  };

  return (
    <div className="flex items-center justify-between px-2">
      <div className="text-muted-foreground flex-1 text-sm">
        {showSelectedRowCount && (
          <T context="showing selected row count in table pagination">
            <Var>{filteredSelectedRowCount}</Var> of{' '}
            <Var>{filteredRowCount}</Var>{' '}
            <Plural n={filteredSelectedRowCount} one="row" other="rows" />{' '}
            selected.
          </T>
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">
            <T context="label for rows per page selector in table pagination">
              Rows per page
            </T>
          </p>
          <Select
            value={`${tableState.pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger
              className={
                pageSizeOptions.some((size) => size >= 100)
                  ? 'h-8 w-[5.5rem]'
                  : 'h-8 w-[70px]'
              }
            >
              <SelectValue placeholder={tableState.pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          <T context="current page indicator in table pagination">
            Page <Var>{pageIndex + 1}</Var> of{' '}
            <Var>{pageCount === -1 ? '?' : pageCount}</Var>
          </T>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => goToPage(0)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">
              <T context="screen reader label for go to first page button in table pagination">
                Go to first page
              </T>
            </span>
            <CaretDoubleLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => goToPage(pageIndex - 1)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">
              <T context="screen reader label for go to previous page button in table pagination">
                Go to previous page
              </T>
            </span>
            <CaretLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => goToPage(pageIndex + 1)}
            disabled={!canNextPage}
          >
            <span className="sr-only">
              <T context="screen reader label for go to next page button in table pagination">
                Go to next page
              </T>
            </span>
            <CaretRightIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden size-8 lg:flex"
            onClick={() => goToPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            <span className="sr-only">
              <T context="screen reader label for go to last page button in table pagination">
                Go to last page
              </T>
            </span>
            <CaretDoubleRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
}
